/* /uploads — every import and how it went.
 *
 * Tab 3 of the old `tasks-import` screen, which was a bare `<table>` with hand-rolled prev/next
 * buttons and `if (!uploadLogs.length) return null` — so a manager who had never imported anything
 * got a blank page with no explanation, and one who had could not search, filter or sort.
 *
 * spec/03 archetype A, on the shared chrome (`lp-*`, `table-cells.js`, `PeriodPicker`), with two
 * deliberate absences:
 *
 * - **No `<Segmented>`.** The endpoint returns no per-status counts, and a segment without them
 *   makes the user click to discover emptiness. Status is a per-row glyph instead. Adding counts
 *   is `_segment_counts` on the ViewSet, i.e. a backend change.
 * - **No bulk bar.** There is no bulk endpoint, and the only row action is destructive.
 *
 * This page is the other half of `/import`: both `tasks/bulk_create/` and
 * `assortment_matrix/bulk_create/` answer `{action, status: "pending"}` and hand off to Celery, so
 * this is the only place the outcome of an import can honestly be read.
 */

import React, { useEffect, useMemo, useRef, useState } from "react";
import { useSelector } from "react-redux";
import { useHistory, useLocation } from "react-router-dom";
import { Button, Card, Col, Drawer, Empty, Flex, Form, Input, Modal, Row, Select, Table, Tag, Typography } from "antd";
import {
  CheckCircleOutlined,
  ClockCircleOutlined,
  CloseCircleOutlined,
  DownloadOutlined,
  ExclamationCircleOutlined,
  ReloadOutlined,
  SearchOutlined,
  StopOutlined,
  UploadOutlined,
} from "@ant-design/icons";

import Alert from "/src/util/alert";

import PeriodPicker, { clampRange, defaultRange } from "../../component/PeriodPicker";
import { RowActions, StatusIcon, TableFoot, TitleCell, actionsColumn, dash, statusColumn, useTableScrollY } from "../../component/table-cells";
import { ENTITY_DROPDOWN_SETTINGS, IMPORT, PHOTO_REPORT, SIDE_NAV } from "../../config/lang";
import { downloadCsv } from "./csv";
import { hasLogs, logsToCsv, readLogs } from "./logs";
import { call, getAbortable } from "../../util/action";
import dayjs from "../../util/dayjs";
import { fill, translate } from "../../util/util";
import { localeFor } from "../merchandiser/route/dateUtils";

import "./import.scss";

const PAGE_SIZES = [10, 20, 50, 100];

/** Widest period the list will accept, matching `/answers` and `/task`. */
const MAX_RANGE_MONTHS = 3;

/* `Action.Type` — there are exactly two, and no code path produces a third. */
const TYPES = [
  ["TASKS_CREATION", "type_tasks"],
  ["ASSORTMENT_MATRIX_CREATION", "type_matrix"],
];

/* `Action.STATUS`, in the order a batch moves through it. */
const STATUSES = [
  ["received", "st_received"],
  ["pending", "st_pending"],
  ["done", "st_done"],
  ["failed", "st_failed"],
  ["deleted", "st_deleted"],
];

/* Tone per state. `deleted` is `off` — the import's data was removed on purpose, which is a
   resting state, not a fault. */
const STATUS_ICON = {
  received: { Icon: ClockCircleOutlined, tone: "wait", key: "st_received" },
  pending: { Icon: ClockCircleOutlined, tone: "wait", key: "st_pending" },
  done: { Icon: CheckCircleOutlined, tone: "ok", key: "st_done" },
  failed: { Icon: CloseCircleOutlined, tone: "no", key: "st_failed" },
  deleted: { Icon: StopOutlined, tone: "off", key: "st_deleted" },
};

/** Column key → the field the API orders on. A key missing here is NOT sortable. */
const ORDERING = { name: "name", type: "type", created: "created", status: "status" };

const FIELD_COL = { xs: { flex: "0 0 100%" }, sm: { flex: "0 0 50%" }, lg: { flex: "0 0 25%" } };

const isoDay = date => dayjs(date).format("YYYY-MM-DD");

/** Still moving, so the list is worth re-reading. */
const isRunning = row => row?.status === "pending" || row?.status === "received";

/** How often to re-read while anything is still processing. */
const POLL_MS = 5000;

export function buildQuery({ term, type, status, range, page, size, sort }) {
  const params = new URLSearchParams();
  const period = clampRange(range, MAX_RANGE_MONTHS);
  params.set("page", String(page));
  params.set("page_size", String(size));
  if (term.trim()) params.set("search", term.trim());
  if (type) params.set("type", type);
  if (status) params.set("status", status);
  /* Plain dates on both ends, inclusive — the filter compares `created__date`, so the picked last
     day is included rather than cut off at its midnight. */
  if (period?.[0]) params.set("created_after", isoDay(period[0]));
  if (period?.[1]) params.set("created_before", isoDay(period[1]));
  if (sort) params.set("ordering", `${sort.dir === "descend" ? "-" : ""}${ORDERING[sort.key]}`);
  return params.toString();
}

const Uploads = () => {
  const history = useHistory();
  const location = useLocation();
  const lang = useSelector(state => state.me?.language) || "en";

  const t = key => translate(IMPORT, key, lang);
  const td = key => translate(ENTITY_DROPDOWN_SETTINGS, key, lang);
  const tp = key => translate(PHOTO_REPORT, key, lang);

  /* The action just submitted, handed over by `/import`. Read once: it marks a row, it is not
     state the page maintains. */
  const justNew = useRef(new URLSearchParams(location.search).get("new")).current;

  const [term, setTerm] = useState("");
  const [search, setSearch] = useState("");
  const [type, setType] = useState(null);
  const [status, setStatus] = useState(null);
  const [range, setRangeState] = useState(defaultRange);
  const setRange = next => setRangeState(clampRange(next, MAX_RANGE_MONTHS) || defaultRange());
  const [page, setPage] = useState(1);
  const [size, setSize] = useState(PAGE_SIZES[0]);
  const [sort, setSort] = useState(null);

  const [rows, setRows] = useState([]);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(true);
  const [failed, setFailed] = useState(false);
  /* The action whose error log is open, or null. */
  const [showing, setShowing] = useState(null);
  /* Bumped to force a refetch without changing the query. */
  const [tick, setTick] = useState(0);

  const filtered = Boolean(search.trim() || type || status);

  const query = useMemo(() => buildQuery({ term: search, type, status, range, page, size, sort }), [search, type, status, range, page, size, sort]);

  useEffect(() => {
    const controller = new AbortController();
    setLoading(true);
    getAbortable(`actions/?${query}`, controller.signal)
      .then(response => {
        if (!response) return;
        setRows(response.results || []);
        setTotal(response.count || 0);
        setFailed(false);
        setLoading(false);
      })
      .catch(error => {
        if (error.name === "AbortError") return;
        setRows([]);
        setTotal(0);
        setFailed(true);
        setLoading(false);
      });
    return () => controller.abort();
  }, [query, tick]);

  /* Only the free-text field is debounced; every other control applies on change. */
  useEffect(() => {
    if (term === search) return;
    const timer = setTimeout(() => {
      setPage(1);
      setSearch(term);
    }, 250);
    return () => clearTimeout(timer);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [term]);

  /* Re-read while anything on the loaded page is still processing, and stop the moment nothing is.
     Landing here straight from an import is the case this exists for — otherwise the row you were
     sent to look at sits on "Processing" until you reload by hand.

     ⚠ Until picsell-api stops deriving the status on read, every row reads `done` immediately and
     this simply never runs. That is harmless, and it starts working when the fix ships. */
  useEffect(() => {
    if (!rows.some(isRunning)) return;
    const timer = setTimeout(() => setTick(value => value + 1), POLL_MS);
    return () => clearTimeout(timer);
  }, [rows]);

  const narrow = fn => (...args) => {
    setPage(1);
    fn(...args);
  };

  const resetFilters = narrow(() => {
    setTerm("");
    setSearch("");
    setType(null);
    setStatus(null);
    setRange(defaultRange());
    setSort(null);
  });

  /* ------------------------------------------------------------- row actions */

  const removeData = row => {
    /* This HARD-DELETES the tasks or assortment matrices the import created; only the upload
       record itself survives. The screen this replaces fired it straight off a trash button with
       no confirmation at all.

       `Modal.confirm` is a static call — it renders outside the tree and does not inherit
       AppShell's `ConfigProvider locale`, so the button labels are passed explicitly or they stay
       English. The confirm names WHAT is going and the button carries the verb (spec/03, spec/04). */
    Modal.confirm({
      title: t("delete_data"),
      icon: <ExclamationCircleOutlined />,
      content: fill(translate(IMPORT, row.type === "ASSORTMENT_MATRIX_CREATION" ? "delete_confirm_matrix" : "delete_confirm_tasks", lang), {
        name: row.name?.trim() || "—",
      }),
      okText: t("delete_data"),
      okButtonProps: { danger: true },
      cancelText: t("cancel"),
      onOk: () =>
        new Promise(resolve => {
          call({ method: "DELETE", url: `actions/${row.id}/delete_action_data/` }, () => {
            Alert.success(t("deleted_msg"), { position: "top-right" });
            /* The row is not removed server-side — its status becomes `deleted` — so the page is
               re-read rather than patched. */
            setTick(value => value + 1);
            resolve();
          });
        }),
    });
  };

  const exportLogs = row => {
    const stamp = row.created ? dayjs(row.created).format("YYYY-MM-DD-HHmm") : "upload";
    downloadCsv(logsToCsv(row, t), `${stamp}-errors.csv`);
  };

  /* ---------------------------------------------------------------- columns */

  const dateFmt = useMemo(() => new Intl.DateTimeFormat(localeFor(lang), { day: "2-digit", month: "short", hour: "2-digit", minute: "2-digit" }), [lang]);

  const columns = useMemo(
    () => [
      statusColumn((value, row) => {
        const state = STATUS_ICON[row.status] || STATUS_ICON.received;
        return <StatusIcon Icon={state.Icon} tone={state.tone} label={t(state.key)} />;
      }),
      {
        key: "name",
        title: t("u_col_name"),
        width: 320,
        fixed: "left",
        ellipsis: true,
        sorter: true,
        render: (value, row) => <TitleCell title={row.name} sub={row.id === justNew ? t("new_row") : null} />,
      },
      {
        key: "type",
        title: t("u_col_type"),
        width: 220,
        ellipsis: true,
        sorter: true,
        render: (value, row) => {
          const known = TYPES.find(([key]) => key === row.type);
          return known ? <Tag>{t(known[1])}</Tag> : dash;
        },
      },
      {
        key: "created",
        title: t("u_col_created"),
        width: 180,
        sorter: true,
        render: (value, row) => <span className="lp-num">{row.created ? dateFmt.format(new Date(row.created)) : "—"}</span>,
      },
      {
        key: "errors",
        title: t("u_col_errors"),
        width: 170,
        render: (value, row) => {
          if (!hasLogs(row)) return dash;
          const { kind, rows: logRows } = readLogs(row);
          return (
            <Button type="link" size="small" onClick={() => setShowing(row)}>
              {kind === "opaque" ? t("view_logs") : <span className="lp-num">{logRows.length}</span>}
            </Button>
          );
        },
      },
      actionsColumn((value, row) => (
        <RowActions
          label={t("row_actions")}
          items={[
            ...(hasLogs(row) ? [{ key: "logs", icon: <ExclamationCircleOutlined />, label: t("view_logs") }] : []),
            ...(hasLogs(row) ? [{ key: "csv", icon: <DownloadOutlined />, label: t("download_logs") }] : []),
            ...(row.status !== "deleted" ? [{ key: "remove", icon: <StopOutlined />, label: t("delete_data"), danger: true }] : []),
          ]}
          onClick={key => {
            if (key === "logs") return setShowing(row);
            if (key === "csv") return exportLogs(row);
            removeData(row);
          }}
        />
      )),
    ],
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [lang, dateFmt, justNew]
  );

  const scrollY = useTableScrollY();

  /* ------------------------------------------------------------- error log */

  const opened = showing ? readLogs(showing) : null;

  const logColumns = useMemo(() => {
    if (opened?.kind === "matrices") {
      return [
        { key: "point", dataIndex: "point", title: t("col_point"), width: 180, ellipsis: true },
        {
          key: "products",
          title: t("col_products"),
          width: 100,
          align: "right",
          render: (value, row) => <span className="lp-num">{row.products.length}</span>,
        },
        { key: "reason", dataIndex: "reason", title: t("col_reason"), ellipsis: true, className: "up-logs__reason" },
      ];
    }
    return [
      { key: "title", dataIndex: "title", title: t("col_title"), width: 200, ellipsis: true },
      { key: "point", dataIndex: "point", title: t("col_point"), width: 130, ellipsis: true },
      { key: "network", dataIndex: "network", title: t("col_network"), width: 130, ellipsis: true },
      { key: "start", dataIndex: "start", title: t("col_start"), width: 130, ellipsis: true },
      { key: "end", dataIndex: "end", title: t("col_end"), width: 130, ellipsis: true },
      { key: "reason", dataIndex: "reason", title: t("col_reason"), width: 260, ellipsis: true, className: "up-logs__reason" },
    ];
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [opened?.kind, lang]);

  return (
    <Flex vertical gap="middle" className="shell-content lp up">
      <Flex className="lp-head" justify="space-between" align="flex-end" gap="middle" wrap>
        <Flex vertical>
          <Typography.Title level={1} className="lp-title">
            {translate(SIDE_NAV, "my_uploads", lang)}
          </Typography.Title>
          <Typography.Paragraph type="secondary" className="lp-sub">
            {t("uploads_sub")}
          </Typography.Paragraph>
        </Flex>
        <Button type="primary" icon={<UploadOutlined />} onClick={() => history.push("/import")}>
          {t("page_title")}
        </Button>
      </Flex>

      <Card className="lp-filters" variant="outlined">
        <Form layout="vertical" component={false} colon={false}>
          <Row gutter={[12, 12]}>
            <Col {...FIELD_COL}>
              <Form.Item label={tp("period")}>
                {/* No `allowFuture`: an import happened, so it cannot have happened tomorrow. */}
                <PeriodPicker value={range} onChange={narrow(setRange)} maxMonths={MAX_RANGE_MONTHS} clearTo={defaultRange} />
              </Form.Item>
            </Col>
            <Col {...FIELD_COL}>
              <Form.Item label={t("f_type")}>
                {/* A closed list of two, so a plain Select rather than the paged EntitySelect. */}
                <Select
                  allowClear
                  value={type}
                  onChange={narrow(setType)}
                  placeholder={t("f_type")}
                  options={TYPES.map(([value, key]) => ({ value, label: t(key) }))}
                />
              </Form.Item>
            </Col>
            <Col {...FIELD_COL}>
              <Form.Item label={t("f_status")}>
                <Select
                  allowClear
                  value={status}
                  onChange={narrow(setStatus)}
                  placeholder={t("f_status")}
                  options={STATUSES.map(([value, key]) => ({ value, label: t(key) }))}
                />
              </Form.Item>
            </Col>
            <Col {...FIELD_COL}>
              <Form.Item label={tp("search")}>
                <Input allowClear prefix={<SearchOutlined />} placeholder={t("f_search_ph")} value={term} onChange={event => setTerm(event.target.value)} />
              </Form.Item>
            </Col>
          </Row>

          <Flex className="lp-filters__foot" align="center" gap="small" wrap>
            <span className="lp-spacer" />
            <Button size="small" icon={<ReloadOutlined />} onClick={() => setTick(value => value + 1)}>
              {t("refresh")}
            </Button>
            <Button size="small" onClick={resetFilters}>
              {td("filter_reset")}
            </Button>
          </Flex>
        </Form>
      </Card>

      <Card className="lp-table" variant="outlined">
        <Flex className="lp-table__bar" align="center" gap="small" wrap>
          <Typography.Text strong className="lp-table__title">
            {translate(SIDE_NAV, "my_uploads", lang)}
          </Typography.Text>
          <Typography.Text type="secondary" aria-live="polite">
            {tp("records_count")}: <Typography.Text strong>{total}</Typography.Text>
          </Typography.Text>
        </Flex>

        <Table
          className="lp-table__grid"
          rowKey="id"
          size="middle"
          columns={columns}
          dataSource={rows}
          loading={loading}
          scroll={{ x: 1100, y: scrollY }}
          rowClassName={row => (row.id === justNew ? "lp-row up-row--new" : "lp-row")}
          onChange={(pagination, filters, sorter) => {
            const next = sorter?.order && ORDERING[sorter.columnKey] ? { key: sorter.columnKey, dir: sorter.order } : null;
            if (JSON.stringify(next) === JSON.stringify(sort)) return;
            setSort(next);
            setPage(1);
          }}
          pagination={false}
          locale={{
            emptyText: (
              <Empty
                image={Empty.PRESENTED_IMAGE_SIMPLE}
                description={
                  /* Three different causes, three different offers — "nothing yet", "nothing
                     matches", and "the request failed" are not the same state (spec/03). */
                  <Flex vertical gap={8} align="center">
                    <Typography.Text strong>{failed ? t("submit_failed") : filtered ? t("empty_filtered_title") : t("empty_title")}</Typography.Text>
                    <Typography.Text type="secondary">{failed ? "" : filtered ? t("empty_filtered_desc") : t("empty_desc")}</Typography.Text>
                    {failed ? (
                      <Button size="small" icon={<ReloadOutlined />} onClick={() => setTick(value => value + 1)}>
                        {t("refresh")}
                      </Button>
                    ) : filtered ? (
                      <Button size="small" onClick={resetFilters}>
                        {td("filter_reset")}
                      </Button>
                    ) : (
                      <Button size="small" type="primary" onClick={() => history.push("/import")}>
                        {t("go_import")}
                      </Button>
                    )}
                  </Flex>
                }
              />
            ),
          }}
        />

        <TableFoot
          id="up"
          page={page}
          size={size}
          total={total}
          sizes={PAGE_SIZES}
          labels={{ perPage: t("rows_per_page"), range: t("range_of") }}
          onPage={setPage}
          onSize={next => {
            setSize(next);
            setPage(1);
          }}
        />
      </Card>

      {/* The failures as a TABLE. The old page could only hand you a downloaded CSV, so the one
          thing you need after a failed import — which rows, and why — required leaving the app. */}
      <Drawer
        open={Boolean(showing)}
        onClose={() => setShowing(null)}
        /* antd v6 deprecated `width` in favour of `size` (default 378 / large 736); the task log is
           six columns wide, so it scrolls inside its own table rather than widening the panel. */
        size="large"
        title={t("logs_title")}
        extra={
          showing && opened?.kind !== "opaque" ? (
            <Button size="small" icon={<DownloadOutlined />} onClick={() => exportLogs(showing)}>
              {t("download_logs")}
            </Button>
          ) : null
        }>
        {opened?.kind === "opaque" ? (
          <Typography.Paragraph className="up-logs__opaque">{t("logs_opaque")}</Typography.Paragraph>
        ) : opened?.kind === "message" ? (
          <Typography.Paragraph className="up-logs__reason">{opened.rows[0].reason}</Typography.Paragraph>
        ) : (
          <Table
            className="lp-table__grid"
            rowKey="key"
            size="small"
            columns={logColumns}
            dataSource={opened?.rows || []}
            scroll={{ x: 900 }}
            pagination={{ pageSize: 50, showSizeChanger: false, size: "small", hideOnSinglePage: true }}
          />
        )}
      </Drawer>
    </Flex>
  );
};

export default Uploads;
